import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";

import PatDagEditor from "./PatDagEditor";
import { confirmDag } from "../../lib/api";
import { detectCausalPattern } from "./patPatternDetector";
import PatternHelperPanel from "./PatternHelperPanel";
import PatResultPanel from "./PatResultPanel";

/* -----------------------------------------------------------
   PatCausalWorkspace.jsx - BCSM / ESCp interpretation helper
----------------------------------------------------------- */

export default function PatCausalWorkspace({ patResult, onUpdated }) {
  if (!patResult || typeof patResult !== "object") return null;

  /* -----------------------------------------------------------
     Resolve root container
  ----------------------------------------------------------- */
  const root = patResult || {};
  const pipe = patResult?.pipeline_result || root;
  const originalQuery =
    root?.raw_input ||
    root?.pipeline_result?.raw_input ||
    root?.raw?.raw_input ||
    patResult?.originalQuery ||
    "";

  // ─────────────────────────────────────────────
  // DAG confirmation state
  // ─────────────────────────────────────────────
  const isDagConfirmed =
    patResult?.confirmed_dag === true ||
    patResult?.pipeline_result?.confirmed_dag === true;

  // ─────────────────────────────────────────────
  // Distillation gating:
  // Only block workspace if distillation is still pending confirmation.
  // DO NOT block once engine_phase has moved to dag_construction.
  // ─────────────────────────────────────────────
  const dist =
    pipe?.pat_distillation ||
    root?.pat_distillation ||
    root?.pipeline_result?.pat_distillation ||
    null;

  const distStatus = dist?.status || null;

  const distillationStillBlocking =
    distStatus === "multiple_outcomes_detected" ||
    distStatus === "awaiting_authorial_distillation" ||
    dist?.user_acknowledged === false;

  if (distillationStillBlocking && root?.engine_phase !== "dag_construction") {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-900/70 p-4 text-slate-200">
        <h3 className="text-sm font-semibold text-amber-300 mb-2">
          Outcome selection required (Pre-DAG)
        </h3>

        <p className="text-sm text-slate-300 mb-3">
          Multiple candidate outcomes were detected. PAT requires exactly one
          outcome (Y) before DAG construction and post-DAG computation can
          proceed.
        </p>

        {Array.isArray(dist?.candidate_outcomes) &&
          dist.candidate_outcomes.length > 0 && (
            <ul className="list-disc list-inside text-sm text-slate-200 space-y-1">
              {dist.candidate_outcomes.map((o, i) => (
                <li key={i}>{o}</li>
              ))}
            </ul>
          )}
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // HARD STOP: complex input requires user disambiguation
  // ─────────────────────────────────────────────
  const outcomeResolved =
    root?.pat_claim_frame?.Y ||
    root?.pat_variables?.outcome ||
    root?.pipeline_result?.pat_variables?.outcome;

  const blockDag =
    (root?.constraints?.dag_construction_blocked === true ||
      pipe?.constraints?.dag_construction_blocked === true) &&
    !outcomeResolved;

  if (blockDag) {
    return null;
  }

  // 🔥 HARD OVERRIDE: if distillation partition exists, it is authoritative.
  // Must prefer USER-EDITED partition over LLM suggestion.
  const distPartition =
    dist?.user_partition ||
    dist?.confirmed_partition ||
    dist?.partition ||
    dist?.acknowledged_partition ||
    dist?.suggested_partition ||
    null;

  const varsFromPartition =
    distPartition &&
    ((Array.isArray(distPartition.X) && distPartition.X.length > 0) ||
      (Array.isArray(distPartition.M) && distPartition.M.length > 0) ||
      (Array.isArray(distPartition.Z) && distPartition.Z.length > 0) ||
      distPartition.Y)
      ? {
          status: "ok",
          mode: "distillation_partition",

          predictors: (distPartition.X || []).map((label, i) => ({
            id: `X${i + 1}`,
            label,
          })),

          mediators: (distPartition.M || []).map((label, i) => ({
            id: `M${i + 1}`,
            label,
          })),

          confounders: (distPartition.Z || []).map((label, i) => ({
            id: `Z${i + 1}`,
            label,
          })),

          outcome: distPartition.Y ? { id: "Y", label: distPartition.Y } : null,
        }
      : null;

  // --------------------------------------------------
  // Claim-frame fallback (initial_extraction stage)
  // --------------------------------------------------
  const claimFrame =
    root?.pat_claim_frame || root?.pipeline_result?.pat_claim_frame || null;

  const claimFrameVars =
    claimFrame?.X && claimFrame?.Y
      ? {
          status: "ok",
          mode: "claim_frame_extraction",

          predictors: [
            {
              id: "X1",
              label: claimFrame.X,
            },
          ],

          mediators: [],
          confounders: [],

          outcome: {
            id: "Y",
            label: claimFrame.Y,
          },
        }
      : null;

  const vars = isDagConfirmed
    ? pipe?.pat_variables ||
      root?.pat_variables ||
      claimFrameVars ||
      root?.raw?.pat_variables ||
      patResult?.pat_variables ||
      {}
    : varsFromPartition ||
      pipe?.pat_variables ||
      root?.pat_variables ||
      claimFrameVars ||
      root?.raw?.pat_variables ||
      patResult?.pat_variables ||
      {};

  const tensor =
    root.pat_tensor || root.raw?.pat_tensor || patResult.pat_tensor || null;

  const causalModel =
    root.pat_causal_model ||
    root.raw?.pat_causal_model ||
    patResult.pat_causal_model ||
    null;

  const rawAtoms =
    root.pat_vector_atoms?.atoms ||
    root.raw?.pat_vector_atoms?.atoms ||
    patResult.pat_vector_atoms?.atoms ||
    [];

  const predictorsRaw = Array.isArray(vars.predictors) ? vars.predictors : [];
  const mediatorsRaw = Array.isArray(vars.mediators) ? vars.mediators : [];
  const confoundersRaw = Array.isArray(vars.confounders)
    ? vars.confounders
    : [];
  const outcomeRaw = vars.outcome || null;

  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [confirmedGraph, setConfirmedGraph] = useState(null);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [showReturnToAudit, setShowReturnToAudit] = useState(false);
  const [reversalAcknowledged, setReversalAcknowledged] = useState(false);
  const [semanticInversionPrompt, setSemanticInversionPrompt] = useState(null);
  const [showDagConnectionHelp, setShowDagConnectionHelp] = useState(false);

  const confirmedDagRef = useRef(null);
  const auditAnchorRef = useRef(null);
  const auditSectionRef = useRef(null);
  const AUDIT_SCROLL_OFFSET = 40;
  const AUDIT_SCROLL_DURATION_MS = 600;
  function getSemanticComparison(payload) {
    if (!payload || typeof payload !== "object") return null;

    return (
      payload?.semantic_structure_comparison ||
      payload?.pipeline_result?.semantic_structure_comparison ||
      payload?.pat_claim_evidence_audit?.semantic_structure_comparison ||
      payload?.pipeline_result?.pat_claim_evidence_audit
        ?.semantic_structure_comparison ||
      null
    );
  }

  function normaliseSemanticText(value) {
    if (typeof value !== "string") return "";

    return value
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/\s*→\s*/g, " -> ")
      .replace(/\bleads to\b/g, "causes")
      .trim();
  }

  function semanticEndpointChanged(comparison) {
    if (!comparison || typeof comparison !== "object") return false;

    const semanticY =
      comparison?.semantic_direction?.Y ||
      comparison?.semantic_direction?.target ||
      "";

    const confirmedY =
      comparison?.confirmed_direction?.Y ||
      comparison?.confirmed_direction?.target ||
      "";

    const semanticEndpoint = normaliseSemanticText(semanticY);
    const confirmedEndpoint = normaliseSemanticText(confirmedY);

    if (!semanticEndpoint || !confirmedEndpoint) return false;

    return semanticEndpoint !== confirmedEndpoint;
  }

  function semanticPromptKind(comparison) {
    if (!comparison || typeof comparison !== "object") {
      return "structure";
    }

    if (
      comparison?.direction_reversed === true &&
      semanticEndpointChanged(comparison)
    ) {
      return "direction";
    }

    return "pathway";
  }

  function splitSemanticLabels(value) {
    if (typeof value !== "string" || !value.trim()) return [];
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }

  function reassignRolesFromSemanticDirection(graphState, semanticDirection) {
    if (!graphState || !Array.isArray(graphState.nodes)) return null;
    if (!semanticDirection || typeof semanticDirection !== "object")
      return null;

    const semanticFrom = splitSemanticLabels(semanticDirection.from);
    const semanticTo = splitSemanticLabels(semanticDirection.to);

    if (!semanticFrom.length || !semanticTo.length) return null;

    const fromSet = new Set(semanticFrom.map((v) => v.toLowerCase()));
    const toSet = new Set(semanticTo.map((v) => v.toLowerCase()));

    const nextNodes = graphState.nodes.map((node) => {
      const label = typeof node?.label === "string" ? node.label.trim() : "";
      const key = label.toLowerCase();

      if (toSet.has(key)) {
        return {
          ...node,
          role: "Y",
          id: node.id || "Y",
        };
      }

      if (fromSet.has(key)) {
        return {
          ...node,
          role: "X",
        };
      }

      return node;
    });

    return {
      ...graphState,
      nodes: nextNodes,
    };
  }

  const hasVariables =
    isDagConfirmed ||
    ["dag_construction", "post_dag_results"].includes(root?.engine_phase) ||
    ["dag_construction", "post_dag_results"].includes(pipe?.engine_phase) ||
    (predictorsRaw.length > 0 && !!outcomeRaw?.label) ||
    (!!distPartition && !!distPartition.Y);
  const shouldBypassScope =
    !root?.pat_scope && tensor?.causal === true && vars?.status === "pending";

  /* -----------------------------------------------------------
     Normalise variables
  ----------------------------------------------------------- */
  const normalisedNodes = useMemo(() => {
    const out = [];

    predictorsRaw.forEach((p, i) =>
      out.push({
        id: p.id || `X${i + 1}`,
        label: p.label,
        role: "X",
      }),
    );

    mediatorsRaw.forEach((m, i) =>
      out.push({
        id: m.id || `M${i + 1}`,
        label: m.label,
        role: "M",
      }),
    );

    confoundersRaw.forEach((z, i) =>
      out.push({
        id: z.id || `Z${i + 1}`,
        label: z.label,
        role: "Z",
      }),
    );

    return out;
  }, [predictorsRaw, mediatorsRaw, confoundersRaw]);

  const normalisedOutcome = useMemo(() => {
    if (!outcomeRaw) return null;
    return {
      id: outcomeRaw.id || "Y",
      label: outcomeRaw.label,
    };
  }, [outcomeRaw]);

  /* -----------------------------------------------------------
         Authoritative variable list (for display)
         Before DAG confirmation: distillation partition remains authoritative.
         After DAG confirmation: backend-returned pat_variables is authoritative,
         because it includes variables added in the DAG editor.
      ----------------------------------------------------------- */
  const displayVariables = useMemo(() => {
    const buildFromNormalisedVariables = () => {
      const out = [];

      if (normalisedOutcome) {
        out.push({
          role: "Y",
          index: null,
          label: normalisedOutcome.label,
        });
      }

      normalisedNodes
        .filter((n) => n.role === "X")
        .forEach((n, i) =>
          out.push({ role: "X", index: i + 1, label: n.label }),
        );

      normalisedNodes
        .filter((n) => n.role === "M")
        .forEach((n, i) =>
          out.push({ role: "M", index: i + 1, label: n.label }),
        );

      normalisedNodes
        .filter((n) => n.role === "Z")
        .forEach((n, i) =>
          out.push({ role: "Z", index: i + 1, label: n.label }),
        );

      return out.filter((v) => typeof v.label === "string" && v.label.trim());
    };

    if (isDagConfirmed) {
      return buildFromNormalisedVariables();
    }

    if (distPartition) {
      const out = [];

      if (distPartition.Y) {
        out.push({ role: "Y", index: null, label: distPartition.Y });
      }

      (distPartition.X || []).forEach((label, i) =>
        out.push({ role: "X", index: i + 1, label }),
      );

      (distPartition.M || []).forEach((label, i) =>
        out.push({ role: "M", index: i + 1, label }),
      );

      (distPartition.Z || []).forEach((label, i) =>
        out.push({ role: "Z", index: i + 1, label }),
      );

      return out.filter((v) => typeof v.label === "string" && v.label.trim());
    }

    return buildFromNormalisedVariables();
  }, [isDagConfirmed, distPartition, normalisedNodes, normalisedOutcome]);

  /* -----------------------------------------------------------
     Graph state (reinitialised when variables appear)
  ----------------------------------------------------------- */
  useEffect(() => {
    setReversalAcknowledged(false);
    setShowReturnToAudit(false);
  }, [
    root?.run_id,
    root?.pipeline_run_id,
    pipe?.run_id,
    pipe?.pipeline_run_id,
  ]);

  useEffect(() => {
    if (!isDagConfirmed) return;

    const confirmed = root?.user_graph || pipe?.user_graph || null;
    if (!confirmed) return;

    setConfirmedGraph((prev) =>
      JSON.stringify(prev) === JSON.stringify(confirmed) ? prev : confirmed,
    );

    setGraph((prev) =>
      JSON.stringify(prev) === JSON.stringify(confirmed) ? prev : confirmed,
    );

    const comparison =
      getSemanticComparison(root) || getSemanticComparison(pipe);
    if (comparison?.requires_user_acknowledgement) {
      setSemanticInversionPrompt(comparison);
    } else {
      setSemanticInversionPrompt(null);
    }
  }, [isDagConfirmed, root?.user_graph, pipe?.user_graph, root, pipe]);

  const varsSigRef = useRef(null);

  const varsSignature = useMemo(() => {
    if (!hasVariables || !normalisedOutcome) return null;
    return JSON.stringify({
      nodes: normalisedNodes,
      y: normalisedOutcome,
    });
  }, [hasVariables, normalisedNodes, normalisedOutcome]);

  useEffect(() => {
    if (isDagConfirmed) return;

    if (!varsSignature || varsSigRef.current === varsSignature) return;
    varsSigRef.current = varsSignature;

    setGraph({
      nodes: [
        ...normalisedNodes,
        {
          id: normalisedOutcome.id,
          label: normalisedOutcome.label,
          role: "Y",
        },
      ],
      edges: [],
    });
  }, [
    varsSignature,
    normalisedNodes,
    normalisedOutcome,
    isDagConfirmed,
    pipe?.user_graph,
  ]);

  /* -----------------------------------------------------------
     BCSM (confirmed DAG only)
  ----------------------------------------------------------- */

  const getGraphNodeRole = useCallback((node) => {
    if (!node || typeof node !== "object") return null;
    return node.role || node?.data?.role || null;
  }, []);

  const yNodeCount = useMemo(
    () =>
      Array.isArray(graph?.nodes)
        ? graph.nodes.filter((n) => getGraphNodeRole(n) === "Y").length
        : 0,
    [graph, getGraphNodeRole],
  );

  function getGraphEdgeTarget(edge) {
    if (!edge) return "";

    // PatDagEditor emits editable graph edges as [source, target].
    if (Array.isArray(edge)) {
      return edge[1] || "";
    }

    if (typeof edge !== "object") return "";

    // Defensive support for object-shaped confirmed or hydrated graph edges.
    return edge.target || edge.to || edge.targetId || "";
  }

  function graphHasIncomingEdgeToY(graphState) {
    const nodes = Array.isArray(graphState?.nodes) ? graphState.nodes : [];
    const edges = Array.isArray(graphState?.edges) ? graphState.edges : [];

    const yNodeIds = new Set(
      nodes
        .filter((node) => getGraphNodeRole(node) === "Y")
        .map((node) => node?.id)
        .filter(Boolean),
    );

    if (yNodeIds.size !== 1) return false;

    return edges.some((edge) => yNodeIds.has(getGraphEdgeTarget(edge)));
  }

  const hasIncomingEdgeToY = graphHasIncomingEdgeToY(graph);

  const hasRoleViolation = graph?.hasRoleViolation === true;
  const graphRoleWarning =
    typeof graph?.roleWarning === "string" ? graph.roleWarning : "";

  const dagRoleConstraintMessage = hasRoleViolation
    ? graphRoleWarning ||
      "A focal outcome (Y) has already been nominated. PAT permits exactly one Y per model. Reassign or remove the existing Y before continuing."
    : yNodeCount === 0
      ? "PAT requires exactly one focal outcome (Y) before DAG confirmation can proceed."
      : yNodeCount > 1
        ? "More than one focal outcome (Y) is currently assigned. PAT permits exactly one Y per model. Reassign or remove the extra outcome before continuing."
        : graph.edges.length > 0 && !hasIncomingEdgeToY
          ? "The selected outcome (Y) is not yet connected to the authorised graph. Add at least one directed edge into Y, such as X → Y or X → M → Y, before confirming the DAG."
          : "";

  const handleGraphChange = useCallback(
    (g) => {
      if (isDagConfirmed) return;

      setGraph((prev) =>
        JSON.stringify(prev) !== JSON.stringify(g) ? g : prev,
      );
    },
    [isDagConfirmed, getGraphNodeRole],
  );

  const handleJumpToConfirmedDag = useCallback(() => {
    if (!confirmedDagRef.current) return;

    confirmedDagRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    setShowReturnToAudit(true);
  }, []);

  const handleReturnToAudit = useCallback(() => {
    const target = document.getElementById("pat-claim-evidence-audit");
    if (!target) return;

    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    window.setTimeout(() => {
      setShowReturnToAudit(false);
    }, AUDIT_SCROLL_DURATION_MS);
  }, []);

  const handleProceedWithReversedDirection = useCallback(() => {
    setReversalAcknowledged(true);
    setSemanticInversionPrompt(null);
  }, []);

  const handleReturnToDagEditing = useCallback(() => {
    const editableGraph = confirmedGraph || graph;

    setConfirmedGraph(null);
    setGraph(editableGraph);
    setShowReturnToAudit(false);
    setReversalAcknowledged(false);
    setSemanticInversionPrompt(null);

    const nextPipe = {
      ...pipe,
      confirmed_dag: false,
      engine_phase: "dag_construction",
      user_graph: editableGraph,
    };

    onUpdated?.({
      ...root,
      confirmed_dag: false,
      user_graph: editableGraph,
      pipeline_result: nextPipe,
    });

    window.requestAnimationFrame(() => {
      confirmedDagRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [confirmedGraph, graph, onUpdated, pipe, root]);

  const handleRevertToSemanticDirection = useCallback(
    ({ semanticDirection }) => {
      const baseGraph = confirmedGraph || graph;
      const nextGraph = reassignRolesFromSemanticDirection(
        baseGraph,
        semanticDirection,
      );

      if (!nextGraph) return;

      const nextPredictors = nextGraph.nodes
        .filter((n) => n.role === "X")
        .map((n, i) => ({
          id: n.id || `X${i + 1}`,
          label: n.label,
        }));

      const nextMediators = nextGraph.nodes
        .filter((n) => n.role === "M")
        .map((n, i) => ({
          id: n.id || `M${i + 1}`,
          label: n.label,
        }));

      const nextConfounders = nextGraph.nodes
        .filter((n) => n.role === "Z")
        .map((n, i) => ({
          id: n.id || `Z${i + 1}`,
          label: n.label,
        }));

      const nextOutcomeNode = nextGraph.nodes.find((n) => n.role === "Y");
      const nextOutcome = nextOutcomeNode
        ? {
            id: nextOutcomeNode.id || "Y",
            label: nextOutcomeNode.label,
          }
        : null;

      setConfirmedGraph(null);
      setGraph(nextGraph);
      setShowReturnToAudit(false);
      setReversalAcknowledged(false);

      const nextPipe = {
        ...pipe,
        engine_phase: "dag_construction",
        confirmed_dag: false,
        user_graph: nextGraph,
        pat_variables: {
          ...(pipe?.pat_variables || root?.pat_variables || {}),
          predictors: nextPredictors,
          mediators: nextMediators,
          confounders: nextConfounders,
          outcome: nextOutcome,
        },
      };

      onUpdated?.({
        ...root,
        confirmed_dag: false,
        user_graph: nextGraph,
        pipeline_result: nextPipe,
      });

      window.requestAnimationFrame(() => {
        confirmedDagRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    },
    [confirmedGraph, graph, onUpdated, pipe, root],
  );

  /* -----------------------------------------------------------
     Confirm DAG
  ----------------------------------------------------------- */
  const handleConfirm = async () => {
    const liveYCount = Array.isArray(graph?.nodes)
      ? graph.nodes.filter((n) => getGraphNodeRole(n) === "Y").length
      : 0;

    const liveRoleViolation = graph?.hasRoleViolation === true;
    const liveHasIncomingEdgeToY = graphHasIncomingEdgeToY(graph);

    if (
      !graph.edges?.length ||
      isRecomputing ||
      liveYCount !== 1 ||
      liveRoleViolation ||
      !liveHasIncomingEdgeToY
    )
      return;

    setIsRecomputing(true);

    // ── extract role-partition ONCE ──
    const predictors = graph.nodes
      .filter((n) => getGraphNodeRole(n) === "X")
      .map((n) => ({ id: n.id, label: n.label }));

    const mediators = graph.nodes
      .filter((n) => getGraphNodeRole(n) === "M")
      .map((n) => ({ id: n.id, label: n.label }));

    const confounders = graph.nodes
      .filter((n) => getGraphNodeRole(n) === "Z")
      .map((n) => ({ id: n.id, label: n.label }));

    const outcomeNode = graph.nodes.find((n) => getGraphNodeRole(n) === "Y");
    if (!outcomeNode) {
      setIsRecomputing(false);
      return;
    }

    const payload = {
      run_id:
        root?.pipeline_run_id ||
        root?.raw?.pipeline_run_id ||
        root?.pipeline_result?.pipeline_run_id ||
        null,

      raw_input:
        root?.raw_input ||
        root?.pipeline_result?.raw_input ||
        root?.raw?.raw_input ||
        "",

      pat_tensor:
        root?.pat_tensor || pipe?.pat_tensor || root?.raw?.pat_tensor || null,

      pat_claim_frame:
        root?.pat_claim_frame ||
        pipe?.pat_claim_frame ||
        root?.raw?.pat_claim_frame ||
        null,

      pat_evidence:
        root?.pat_evidence ||
        pipe?.pat_evidence ||
        root?.raw?.pat_evidence ||
        null,

      pat_evidence_links:
        root?.pat_evidence_links ||
        pipe?.pat_evidence_links ||
        root?.raw?.pat_evidence_links ||
        null,

      pat_variables: {
        predictors,
        mediators,
        confounders,
        outcome: {
          id: outcomeNode.id,
          label: outcomeNode.label,
        },
      },

      confirmed_dag: graph,
      user_graph: graph,
      causal_mode: "auto",
    };

    try {
      const returned = await confirmDag(payload);

      // 🔎 diagnostic: verify which pathway object the renderer receives
      const comparison = getSemanticComparison(returned);
      if (comparison?.requires_user_acknowledgement) {
        setSemanticInversionPrompt(comparison);
        setReversalAcknowledged(false);
      } else {
        setSemanticInversionPrompt(null);
      }

      // IMPORTANT: Do NOT merge raw backend payload manually.
      // Let App.jsx canonicalise it.
      onUpdated?.(returned);
    } finally {
      setIsRecomputing(false);
    }
  };

  /* -----------------------------------------------------------
   Render
----------------------------------------------------------- */
  return (
    <div className="mb-12">
      {/* ─────────────────────────────────────────────
       Phase 2: Causal workspace (DAG + analysis)
       ───────────────────────────────────────────── */}
      {hasVariables && (
        <>
          {/* PAT identity */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-sky-400 mb-1">
              Directed Acyclic Graph (DAG) Construction
            </h1>

            {originalQuery && (
              <p className="text-slate-300 text-sm mt-2">
                Input statement:{" "}
                <span className="text-white">{originalQuery}</span>
              </p>
            )}
          </div>

          <PatternHelperPanel
            pattern={
              !isDagConfirmed && graph.nodes.length
                ? {
                    ...detectCausalPattern(graph.nodes, graph.edges),
                    userClaim:
                      patResult?.raw_input ?? patResult?.originalQuery ?? "",
                    hideUserClaim: false,
                  }
                : null
            }
          />

          {displayVariables.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-amber-300 mb-2">
                {isDagConfirmed
                  ? "Confirmed role assignment"
                  : "Provisional role assignment"}
              </h3>

              <ul className="mb-2 text-gray-200 space-y-1">
                {displayVariables.map((v, i) => (
                  <li key={`${v.role}-${i}`}>
                    {v.role}
                    {v.index !== null ? `${v.index}` : ""}: {v.label}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* DAG instructions */}
          <p className="text-gray-300 max-w-2xl mb-6">
            {isDagConfirmed ? (
              <>
                The Directed Acyclic Graph (DAG) below is the authorised causal
                structure for this PAT run. The confirmed roles and arrows
                define the structure used for confounder analysis, intervention
                reasoning, and stability assessment.
              </>
            ) : (
              <>
                In the Directed Acyclic Graph (DAG) below, each variable is
                shown as a node. Add arrows where you want PAT to treat one
                variable as a hypothesised cause of another. At this stage, you
                are defining the structure that later authorises confounder
                analysis, intervention reasoning, and stability assessment.
              </>
            )}
          </p>

          {!isDagConfirmed && (
            <div className="max-w-2xl mb-4 rounded-lg border border-slate-700 bg-slate-900/60">
              <button
                type="button"
                onClick={() => setShowDagConnectionHelp((v) => !v)}
                className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-sky-300 hover:bg-slate-800/60"
                aria-expanded={showDagConnectionHelp}
              >
                <span>How PAT interprets DAG connections</span>
                <span className="text-slate-400">
                  {showDagConnectionHelp ? "−" : "+"}
                </span>
              </button>

              {showDagConnectionHelp && (
                <div className="border-t border-slate-700 px-4 py-3 text-sm leading-relaxed text-slate-300 space-y-4">
                  <p>
                    PAT interprets only the graph you authorise. A variable may
                    appear in the workspace without being causally operative for
                    the selected outcome. It becomes operative only when it
                    participates in a directed path or back-door structure
                    relevant to{" "}
                    <span className="font-mono text-sky-300">Y</span>.
                  </p>

                  <p className="text-[0.72rem] text-slate-500 leading-snug">
                    Back-door note: in Pearlian terms, a back-door path is a
                    path from <span className="font-mono text-sky-400">X</span>{" "}
                    to <span className="font-mono text-sky-400">Y</span> that
                    starts with an arrow pointing into{" "}
                    <span className="font-mono text-sky-400">X</span>. In PAT,
                    this usually marks a candidate common-cause or adjustment
                    structure, not the proposed{" "}
                    <span className="font-mono text-sky-400">X → Y</span> effect
                    itself.
                  </p>

                  <p>
                    An unconnected{" "}
                    <span className="font-mono text-sky-300">X</span>,{" "}
                    <span className="font-mono text-sky-300">M</span>, or{" "}
                    <span className="font-mono text-sky-300">Z</span> remains
                    available for inspection or audit, but it does not affect
                    causal interpretation, adjustment logic, mediation logic, or
                    intervention reasoning.
                  </p>

                  <div className="rounded-md border border-slate-700 bg-slate-950/60 px-3 py-3 space-y-2">
                    <p className="font-semibold text-sky-300">
                      Role checks before confirming the DAG
                    </p>

                    <ul className="list-disc pl-6 space-y-2 text-slate-300">
                      <li>
                        <span className="font-mono text-sky-300">Y</span>: What
                        is the single outcome this model is trying to explain?
                      </li>

                      <li>
                        <span className="font-mono text-sky-300">X</span>: What
                        variable is being set, changed, chosen, compared, or
                        treated as the candidate X?
                      </li>

                      <li>
                        <span className="font-mono text-sky-300">M</span>: Is
                        this variable downstream of{" "}
                        <span className="font-mono text-sky-300">X</span> and on
                        a pathway into{" "}
                        <span className="font-mono text-sky-300">Y</span>? Ask
                        whether the intended structure is{" "}
                        <span className="font-mono text-sky-300">
                          X → M → Y
                        </span>
                        .
                      </li>

                      <li>
                        <span className="font-mono text-sky-300">Z</span>: Is
                        this variable a background condition or common cause
                        that plausibly influences both{" "}
                        <span className="font-mono text-sky-300">X</span> and{" "}
                        <span className="font-mono text-sky-300">Y</span>? Ask
                        whether the intended structure is{" "}
                        <span className="font-mono text-sky-300">Z → X</span>{" "}
                        and{" "}
                        <span className="font-mono text-sky-300">Z → Y</span>.
                      </li>
                    </ul>
                  </div>

                  <div className="rounded-md border border-slate-700 bg-slate-950/60 px-3 py-3 space-y-2">
                    <p className="font-semibold text-sky-300">
                      Partial edges do not settle variable roles
                    </p>

                    <ul className="list-disc pl-6 space-y-1 text-slate-300">
                      <li>
                        <span className="font-mono text-sky-300">X → M</span>{" "}
                        does not make{" "}
                        <span className="font-mono text-sky-300">M</span> a
                        mediator unless{" "}
                        <span className="font-mono text-sky-300">M → Y</span> is
                        also authorised.
                      </li>

                      <li>
                        <span className="font-mono text-sky-300">X → Z</span>{" "}
                        may make{" "}
                        <span className="font-mono text-sky-300">Z</span>{" "}
                        downstream of{" "}
                        <span className="font-mono text-sky-300">X</span>, but
                        it does not make{" "}
                        <span className="font-mono text-sky-300">Z</span> a
                        confounder for the total effect of{" "}
                        <span className="font-mono text-sky-300">X</span> on{" "}
                        <span className="font-mono text-sky-300">Y</span>.
                      </li>

                      <li>
                        <span className="font-mono text-sky-300">Z</span>{" "}
                        functions as a confounder for the{" "}
                        <span className="font-mono text-sky-300">X → Y</span>{" "}
                        relation only when the graph authorises a back-door
                        structure in which{" "}
                        <span className="font-mono text-sky-300">Z</span> is a
                        common cause of{" "}
                        <span className="font-mono text-sky-300">X</span> and{" "}
                        <span className="font-mono text-sky-300">Y</span>.
                      </li>
                    </ul>
                  </div>

                  <div className="rounded-md border border-slate-700 bg-slate-950/60 px-3 py-3 space-y-2">
                    <p className="font-semibold text-sky-300">
                      Latent versus observed outcomes
                    </p>

                    <p>
                      Some causal questions distinguish an underlying state from
                      the way that state is detected, measured, recorded, or
                      reported. These are not automatically the same outcome.
                    </p>

                    <p>
                      For example, a screening programme may increase recorded
                      diagnoses without increasing the true incidence of
                      disease. In that case, disease incidence, detection
                      probability, and recorded diagnosis may need to be
                      represented as distinct variables.
                    </p>

                    <p>
                      Before confirming the DAG, ask whether{" "}
                      <span className="font-mono text-sky-300">Y</span> is the
                      underlying condition, the observed record, or a downstream
                      measurement process. If detection, reporting, or
                      measurement is downstream of{" "}
                      <span className="font-mono text-sky-300">X</span>, it
                      should not be treated as a confounder for the total effect
                      of <span className="font-mono text-sky-300">X</span> on{" "}
                      <span className="font-mono text-sky-300">Y</span>. It may
                      instead be an outcome, mediator, measurement node, or part
                      of the observation process.
                    </p>
                  </div>

                  <p>
                    Downstream causal processing, including do-calculus,
                    requires at least one authorised directed path into{" "}
                    <span className="font-mono text-sky-300">Y</span>, or
                    another graph structure relevant to{" "}
                    <span className="font-mono text-sky-300">Y</span>.
                  </p>
                </div>
              )}
            </div>
          )}

          {displayVariables.length > 0 && (
            <p className="text-xs text-slate-500 max-w-2xl mb-4">
              Note: Y = outcome variable | X = candidate cause, exposure or
              intervention variable | M = mediator/pathway variable | Z =
              background, contextual, adjustment, or provisional confounding
              variable.
            </p>
          )}

          <div ref={confirmedDagRef}>
            <PatDagEditor
              predictors={isDagConfirmed ? [] : normalisedNodes}
              outcome={isDagConfirmed ? null : normalisedOutcome}
              initialGraph={isDagConfirmed ? confirmedGraph : graph}
              onGraphChange={handleGraphChange}
              locked={isDagConfirmed}
            />
          </div>

          <div className="mt-4 mb-8 flex flex-col items-center gap-3">
            <button
              onClick={handleConfirm}
              disabled={
                !graph.edges.length ||
                isDagConfirmed ||
                isRecomputing ||
                yNodeCount !== 1 ||
                hasRoleViolation ||
                !hasIncomingEdgeToY
              }
              className={`px-5 py-2 rounded text-white transition-all duration-300
                ${
                  isRecomputing
                    ? "bg-emerald-500 animate-pulse"
                    : isDagConfirmed
                      ? "bg-slate-600"
                      : graph.edges.length &&
                          yNodeCount === 1 &&
                          !hasRoleViolation &&
                          hasIncomingEdgeToY
                        ? "bg-blue-600 hover:bg-blue-500"
                        : "bg-slate-700 opacity-50 cursor-not-allowed"
                }`}
            >
              {isDagConfirmed
                ? "DAG Confirmed"
                : isRecomputing
                  ? "Recomputing..."
                  : "Confirm DAG and Recompute"}
            </button>

            {!isDagConfirmed && dagRoleConstraintMessage && (
              <div className="max-w-2xl rounded border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                {dagRoleConstraintMessage}
              </div>
            )}

            {isDagConfirmed && showReturnToAudit && (
              <button
                type="button"
                onClick={handleReturnToAudit}
                className="px-4 py-2 rounded border border-slate-600 text-slate-200 hover:bg-slate-800 transition-all duration-300"
              >
                Return to audit panel
              </button>
            )}
          </div>

          {isDagConfirmed && (
            <div
              ref={auditSectionRef}
              className="mt-8 border-t border-slate-700 pt-6"
              style={{ scrollMarginTop: `${AUDIT_SCROLL_OFFSET}px` }}
            >
              <div
                ref={auditAnchorRef}
                style={{
                  height: 0,
                  scrollMarginTop: `${AUDIT_SCROLL_OFFSET}px`,
                }}
              />

              {semanticInversionPrompt && !reversalAcknowledged && (
                <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-slate-100">
                  {(() => {
                    const promptKind = semanticPromptKind(
                      semanticInversionPrompt,
                    );
                    const isDirectionChange = promptKind === "direction";

                    return (
                      <>
                        <h3 className="mb-2 text-sm font-semibold text-amber-300">
                          {isDirectionChange
                            ? "Confirm semantic direction change"
                            : "Confirm semantic structure change"}
                        </h3>

                        <p className="text-sm text-slate-200 mb-2">
                          {isDirectionChange
                            ? "Your confirmed DAG materially departs from PAT's original semantic X → Y reading of the input. This is advisory only. Please confirm whether that change is intentional."
                            : "Your confirmed DAG retains the focal outcome but changes which intermediate pathways are represented. This is advisory only. Please confirm whether that structural change is intentional."}
                        </p>

                        {semanticInversionPrompt?.semantic_direction?.text && (
                          <p className="text-xs text-slate-300 mb-1">
                            PAT semantic reading:{" "}
                            <span className="text-white">
                              {semanticInversionPrompt.semantic_direction.text}
                            </span>
                          </p>
                        )}

                        {semanticInversionPrompt?.confirmed_direction?.text && (
                          <p className="text-xs text-slate-300 mb-3">
                            Confirmed DAG:{" "}
                            <span className="text-white">
                              {semanticInversionPrompt.confirmed_direction.text}
                            </span>
                          </p>
                        )}

                        <p className="text-xs text-slate-400 mb-4">
                          {isDirectionChange
                            ? semanticInversionPrompt?.structural_notes ||
                              "The confirmed DAG retains a different endpoint direction from PAT's original semantic reading. This may be a valid user-authorised reframing, but it should be acknowledged explicitly before interpretation proceeds."
                            : "The confirmed DAG preserves the focal outcome while retaining, omitting, or reorganising intermediate pathway variables. This may be a valid user-authorised refinement, but it should be acknowledged explicitly before interpretation proceeds."}
                        </p>

                        <div className="flex flex-wrap gap-3">
                          <button
                            type="button"
                            onClick={handleProceedWithReversedDirection}
                            className="px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-500 transition-all duration-300"
                          >
                            {isDirectionChange
                              ? "Proceed with confirmed X ➙ Y configuration"
                              : "Proceed with confirmed structure"}
                          </button>

                          <button
                            type="button"
                            onClick={handleReturnToDagEditing}
                            className="px-4 py-2 rounded border border-slate-600 text-slate-200 hover:bg-slate-800 transition-all duration-300"
                          >
                            Return to edit DAG
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              <PatResultPanel
                pipelineResult={pipe}
                onJumpToConfirmedDag={handleJumpToConfirmedDag}
                reversalAcknowledged={reversalAcknowledged}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
